/**
 * How an alert reads, taken from the server rather than decided here.
 *
 * The UI used to take its arrow, its colour and its word from
 * `anomaly.direction` — which is the RULE'S WATCH FILTER, not what happened —
 * and its number from the raw `delta_pct`. That is how returns falling to zero
 * got an up arrow over "-100%", and how a balance whose prior value was
 * floating-point dust got "22,611,359,704,335%" on a customer's screen.
 *
 * `anomaly.presentation` is computed once in `anomaly_presentation.py` and is
 * the same object the alert title and the context sent to the model are built
 * from, so none of them can contradict the others. `read()` exists so every
 * place in the UI that used to reach for `direction` reads that one source,
 * and so a platform build that predates the field still renders today's
 * behaviour instead of blanks.
 */
import type { Anomaly } from './api';
import { formatPct, formatValue } from './humanize';

export interface AlertReading {
  arrow: 'up' | 'down' | 'flat';
  /** Magnitude as a percentage, or null when a percentage means nothing. */
  pct: number | null;
  /** "Down" / "Up" / "Decreased" / "Shifted toward credit" — title case. */
  verb: string;
  priorValue: number | null;
  currentValue: number | null;
  absoluteChange: number | null;
}

function titleCase(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

export function read(anomaly: Anomaly): AlertReading {
  const told = anomaly.presentation;
  if (told) {
    return {
      arrow: told.arrow,
      pct: told.pct,
      verb: titleCase(told.verb),
      priorValue: told.display_prior,
      currentValue: told.display_current,
      absoluteChange: told.absolute_change
    };
  }
  const down = anomaly.direction === 'DOWN';
  return {
    arrow: down ? 'down' : 'up',
    pct: anomaly.delta_pct,
    verb: down ? 'Down' : 'Up',
    priorValue: anomaly.prior_value,
    currentValue: anomaly.current_value,
    absoluteChange: anomaly.delta
  };
}

/**
 * A signed balance is neither good nor bad: leaning to debit is not a win and
 * leaning to credit is not a loss. Painting one green and the other red would
 * invent a judgement the metric does not carry, so `flat` gets no colour.
 */
export function trendColor(arrow: AlertReading['arrow']): string {
  if (arrow === 'down') return 'text-red-600 dark:text-red-400';
  if (arrow === 'up') return 'text-emerald-600 dark:text-emerald-400';
  return 'text-muted-foreground';
}

export function trendBg(arrow: AlertReading['arrow']): string {
  if (arrow === 'down') return 'bg-red-50 dark:bg-red-950/30';
  if (arrow === 'up') return 'bg-emerald-50 dark:bg-emerald-950/30';
  return 'bg-muted';
}

export function trendBorder(arrow: AlertReading['arrow']): string {
  if (arrow === 'down') return 'border-red-200 dark:border-red-900';
  if (arrow === 'up') return 'border-emerald-200 dark:border-emerald-900';
  return 'border-border';
}

export function arrowGlyph(arrow: AlertReading['arrow']): string {
  return arrow === 'down' ? '↓' : arrow === 'up' ? '↑' : '→';
}

/**
 * The number the alert is known by: "45%" normally, and for a metric whose
 * percentage means nothing, the amount that actually moved. Never an empty
 * space where a figure should be.
 */
export function magnitudeText(reading: AlertReading): string {
  if (reading.pct !== null && reading.pct !== undefined) {
    return formatPct(Math.abs(reading.pct));
  }
  if (reading.absoluteChange) return formatValue(Math.abs(reading.absoluteChange));
  return '—';
}

/** "↓ 45%" — the compact form used in lists and summary tiles. */
export function headlineFigure(anomaly: Anomaly): string {
  const reading = read(anomaly);
  return `${arrowGlyph(reading.arrow)} ${magnitudeText(reading)}`;
}
